import React, { useEffect, useState } from 'react';
import { Alert, Checkbox, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { fetchOrderStockStatus, type OrderStockStatus } from '../../api/orderStockStatusApi';
import {
  getConfirmationNoticeDefaults,
  isRecipientSelected,
  noticeRecipientTitle,
  withRecipientSelected,
  type OrderConfirmationNoticeSelection,
} from '../../confirmationNoticeSelection';
import ConfirmFulfillmentBlock, { type OrderFulfillmentChoice } from './ConfirmFulfillmentBlock';
import ConfirmStockTable from './ConfirmStockTable';
import type { OrderProduct } from '../../../../types/order';
import './OrderGuide.css';

/** Confirmation checks stock and recipients; purchasing is a separate reviewed command (2026-10-14). */
export default function OrderConfirmDialogBody({
  orderId,
  value,
  onChange,
  fulfillment,
  onFulfillmentChange,
  products,
}: {
  orderId: string;
  /** Order lines, for the dimensions under the product names. */
  products?: OrderProduct[];
  value: OrderConfirmationNoticeSelection | null;
  onChange: (value: OrderConfirmationNoticeSelection) => void;
  fulfillment: OrderFulfillmentChoice;
  onFulfillmentChange: (value: OrderFulfillmentChoice) => void;
}) {
  const { t } = useTranslation(['orders']);
  const [status, setStatus] = useState<OrderStockStatus | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setStatus(null);
    setFailed(false);
    fetchOrderStockStatus(orderId)
      .then((loaded) => {
        if (cancelled) return;
        setStatus(loaded);
        if (!value) onChange({ ...getConfirmationNoticeDefaults(loaded), customer: false });
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  if (failed) {
    return <Alert type="warning" showIcon className="zk-dialog-alert" message={t('orders:confirmationNotice.loadError')} />;
  }
  if (!status) return <Spin style={{ display: 'block', margin: '16px auto' }} />;

  // The customer is mailed from the offer step („Wyślij do klienta”, with a preview) — never from here.
  const selection = value || { ...getConfirmationNoticeDefaults(status), customer: false };
  const people = status.noticeRecipients;
  const logisticsLabel = (name: string) => t('orders:process.confirmDialog.logisticsPerson', { name });
  const card = (
    testId: string,
    checked: boolean,
    onToggle: (checked: boolean) => void,
    title: string,
    hint: React.ReactNode,
    options: { disabled?: boolean } = {},
  ) => (
    <label className={`zk-confirm__person${checked ? ' is-checked' : ''}${options.disabled ? ' is-disabled' : ''}`} data-testid={`${testId}-card`}>
      <Checkbox
        checked={checked}
        disabled={options.disabled}
        onChange={(event) => onToggle(event.target.checked)}
        data-testid={testId}
        aria-label={title}
      />
      <span>
        <b>{title}</b>
        <small>{hint}</small>
      </span>
    </label>
  );

  return (
    <div className="zk-confirm" data-testid="order-confirmation-notice">
      <div className="zk-confirm__sect">{t('orders:process.confirmDialog.stockTitle')}</div>
      <ConfirmStockTable lines={status.lines} products={products} testId="zk-confirm-stock-table" />
      <div className="zk-confirm__note">{t('orders:process.confirmDialog.stockNote')}</div>

      <ConfirmFulfillmentBlock orderNumber={status.orderNumber} lines={status.lines} products={products} value={fulfillment} onChange={onFulfillmentChange} />

      <div className="zk-confirm__sect">{t('orders:process.confirmDialog.recipientsTitle')}</div>
      <div className="zk-confirm__people">
        {people.map((person) => card(
          `confirmation-notice-user-${person.userId}`,
          isRecipientSelected(selection, person),
          (checked) => onChange(withRecipientSelected(selection, person, checked)),
          noticeRecipientTitle(person, people, logisticsLabel),
          t(person.role === 'OPERATIONS' ? 'orders:process.confirmDialog.operationsHint' : 'orders:process.confirmDialog.adminsHint'),
        ))}
      </div>
      <div className="zk-confirm__note">{t('orders:process.confirmDialog.recipientsNote')}</div>

      <div className="zk-confirm__sect">{t('orders:supplierGrouping.action')}</div>
      <p>{t('orders:supplierGrouping.confirmationNote')}</p>
    </div>
  );
}
